import React, {
    createContext,
    useContext,
    useState,
    ReactNode,
    useEffect,
    useMemo,
    useRef,
} from 'react';
import {
    getAuxiliaryZinantisMemberData,
    getHotelOwnerZinantisData,
} from '../../services/zinantis/members/zinantisMemberListServices';
import {
    AccountManagementSegmentState,
    ConnectionRequestState,
    MembersFilters,
    MemberState,
    ZinantisRelationshipStatusTypeKeys,
    ZinantisStatusState,
} from '../../types/zinantis/members/membersType';
import { useNotification } from '../../contexts/NotificationsContext';
import useTranslate from '../../customHooks/useTranslate';
import {
    connectedMembersOrder,
    memberDisconnectedStatusColor,
    PENDING_ZINANTIS_RELATIONSHIP_STATUS_TYPE_INITIAL_STATE,
} from '../../utils/zinantis/members/membersUtils';
import { enrichMembersWithConnectionData } from '../../services/zinantis/members/zinantisMemberListAdapters';
import { useSearchParams } from '../../customHooks/useSearchParams';
import { FeedbackMessages } from '../../utils/commonVariables/messages';
import { useIdHotelOwner } from '../../contexts/HotelOwnerContext';
import useLanguage from '../../customHooks/useLanguage';

export const ZINANTIS_AGENCY_CONFIRMED_STATUS_KEY = 'CONFIRMED';

interface ZinantisMembersContextType {
    accountManagementSegments: AccountManagementSegmentState[];
    members: MemberState[];
    zinantisStatuses: ZinantisStatusState[];
    filters: MembersFilters;
    updateFilters: <K extends keyof MembersFilters>(
        _filterKey: K,
        _value: MembersFilters[K]
    ) => void;
    resetFilters: () => void;
    isLoading: boolean;
    handleOwnerZinantisData: () => Promise<void>;
    addConnectedAgency: (
        idAgency: string,
        request: ConnectionRequestState
    ) => void;
    disconnectAgency: (idAgency: string) => void;
    updateRequest: (
        isAgency: string,
        updatedRequest: ConnectionRequestState
    ) => void;
}

const ContextZinantisMembers = createContext<
    ZinantisMembersContextType | undefined
>(undefined);

const INITIAL_STATE_FILTERS: MembersFilters = {
    name: '',
    idCountry: null,
    connexion: 'all',
    idAccountManagementSegment: null,
};

export const ProviderZinantisMembers = ({
    children,
}: {
    children: ReactNode;
}) => {
    const translate = useTranslate();
    const IdHotelOwner = useIdHotelOwner();
    const language = useLanguage();
    const { setErrorMessage } = useNotification();

    const [accountManagementSegments, setAccountManagementSegments] = useState<
        AccountManagementSegmentState[]
    >([]);
    const [members, setMembers] = useState<MemberState[]>([]);
    // los miembros tal cual vienen de la DDBB sin información de si están conectados o no
    const membersDDBB = useRef<MemberState[]>([]);
    const [zinantisStatuses, setZinantisStatuses] = useState<
        ZinantisStatusState[]
    >([]);
    const [filters, setFilters] = useState<MembersFilters>(
        INITIAL_STATE_FILTERS
    );
    const [isLoading, setIsLoading] = useState(true);

    const pendingStatus = useMemo(() => {
        return (
            zinantisStatuses.find(
                (status: ZinantisStatusState) =>
                    status.key === ZinantisRelationshipStatusTypeKeys.PENDING
            ) || PENDING_ZINANTIS_RELATIONSHIP_STATUS_TYPE_INITIAL_STATE
        );
    }, [zinantisStatuses]);

    const updateFilters = <K extends keyof MembersFilters>(
        filterKey: K,
        value: MembersFilters[K]
    ) => {
        setFilters(prevFilters => ({
            ...prevFilters,
            [filterKey]: value,
        }));
    };

    const resetFilters = () => {
        setFilters(INITIAL_STATE_FILTERS);
    };

    const handleAuxiliaryData = async () => {
        const {
            segments,
            members: membersApiDDBB,
            statuses: memberStatuses,
        } = await getAuxiliaryZinantisMemberData();

        setAccountManagementSegments(segments);
        setZinantisStatuses(memberStatuses);
        membersDDBB.current = membersApiDDBB;
    };

    const handleOwnerZinantisData = async () => {
        if (!IdHotelOwner) return;
        const { data } = await getHotelOwnerZinantisData(IdHotelOwner);
        const { ZinantisMembership, Agencies, SupplierHotels } = data.Result;

        if (ZinantisMembership) {
            const connectedMembers = enrichMembersWithConnectionData(
                membersDDBB.current,
                Agencies,
                SupplierHotels
            );
            setMembers(connectedMembers);
        }
    };

    useEffect(() => {
        if (!IdHotelOwner) return;

        const loadData = async () => {
            try {
                setIsLoading(true);
                await handleAuxiliaryData();
                await handleOwnerZinantisData();
                setIsLoading(false);
            } catch (error) {
                console.error(error);
                setErrorMessage(FeedbackMessages.COMMON_ERROR);
            }
        };

        loadData();
    }, [IdHotelOwner, language]);

    const addConnectedAgency = (
        idAgency: string,
        request: ConnectionRequestState
    ) => {
        setMembers(prevMembers => {
            return prevMembers.map(member => {
                if (member.id === idAgency) {
                    return {
                        ...member,
                        status: pendingStatus.text,
                        statusKey: pendingStatus.key,
                        statusColor: pendingStatus.color,
                        connectionRequests: [request],
                    };
                }
                return member;
            });
        });
    };

    const disconnectAgency = (idAgency: string) => {
        setMembers(prevMembers => {
            return prevMembers.map(member => {
                if (member.id === idAgency) {
                    return {
                        ...member,
                        status: translate('disconnected'),
                        statusKey:
                            ZinantisRelationshipStatusTypeKeys.DISCONNECTED,
                        statusColor: memberDisconnectedStatusColor,
                    };
                }
                return member;
            });
        });
    };

    const updateRequest = (
        isAgency: string,
        updatedRequest: ConnectionRequestState
    ) => {
        setMembers(prevMembers => {
            return prevMembers.map(member => {
                if (member.id === isAgency) {
                    const updatedRequests = member.connectionRequests.map(
                        request =>
                            request.id === updatedRequest.id
                                ? { ...request, ...updatedRequest }
                                : request
                    );
                    return {
                        ...member,
                        connectionRequests: updatedRequests,
                    };
                }
                return member;
            });
        });
    };

    return (
        <ContextZinantisMembers.Provider
            value={{
                accountManagementSegments,
                members,
                filters,
                updateFilters,
                resetFilters,
                isLoading,
                zinantisStatuses,
                handleOwnerZinantisData,
                addConnectedAgency,
                disconnectAgency,
                updateRequest,
            }}
        >
            {children}
        </ContextZinantisMembers.Provider>
    );
};

export const useZinantisMembers = () => {
    const context = useContext(ContextZinantisMembers);
    if (!context) {
        throw new Error(
            'useZinantisMembers debe usarse dentro de un ZinantisMembersProvider'
        );
    }

    const { members, filters, accountManagementSegments } = context;

    const getFilteredMembers = (_members: MemberState[]) => {
        let filtered = [..._members];

        if (filters.idCountry) {
            filtered = filtered.filter(
                member => member.idCountry === filters.idCountry
            );
        }
        if (filters.name) {
            filtered = filtered.filter(member =>
                member?.name
                    ?.toLocaleLowerCase()
                    .includes(filters.name.toLowerCase())
            );
        }
        if (filters.connexion !== 'all') {
            filtered = filtered.filter(member => {
                return member.statusKey === filters.connexion;
            });
        }
        if (filters.idAccountManagementSegment !== null) {
            filtered = filtered.filter(member => {
                return (
                    member.idAccountManagementSegment ===
                    filters.idAccountManagementSegment
                );
            });
        }
        return filtered;
    };

    const membersWithoutSegment = useMemo(() => {
        const validSegmentIds = accountManagementSegments.map(s =>
            parseInt(s.id)
        );
        return members.filter(member => {
            return (
                !member.idAccountManagementSegment ||
                !validSegmentIds.includes(member.idAccountManagementSegment)
            );
        });
    }, [members, accountManagementSegments]);

    const connectedMembers = useMemo(() => {
        return members
            .filter(
                member =>
                    member.statusKey !==
                    ZinantisRelationshipStatusTypeKeys.DISCONNECTED
            )
            .sort((a, b) => {
                const orderA = connectedMembersOrder[a.statusKey] || 99;
                const orderB = connectedMembersOrder[b.statusKey] || 99;
                if (orderA !== orderB) return orderA - orderB;

                return a.name.localeCompare(b.name, undefined, {
                    sensitivity: 'base',
                });
            });
    }, [members]);

    const newMembers = useMemo(() => {
        return members.filter(member => member.isNew);
    }, [members]);

    const { getSearchParam, removeSearchParam } = useSearchParams({
        base64: true,
    });

    const idUrl = getSearchParam('id');

    const openedPanelMember = useMemo(() => {
        return members.find(member => member.id === idUrl);
    }, [members, idUrl]);

    useEffect(() => {
        if (!openedPanelMember && idUrl && members.length) {
            removeSearchParam('id');
        }
    }, [openedPanelMember, idUrl, members]);

    return {
        ...context,
        filteredMembers: getFilteredMembers(members),
        membersWithoutSegment,
        connectedMembers,
        newMembers,
        getFilteredMembers,
        openedPanelMember,
    };
};
